import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { statusTone } from './mccConstants';

/**
 * Renders a collapsible group with a coloured status header.
 * Used to build "group by status" list views.
 *
 * Props:
 *   status: string status key (used for label + colour)
 *   count: number of rows
 *   defaultOpen?: bool (default true if count > 0)
 *   children: table or list markup
 *   sticky?: bool  — if true, header sticks to top on scroll
 *   selectAll?: { checked, indeterminate, onChange }  — when provided, renders a "select all in group" checkbox on the header
 */
export default function MccStatusGroup({
  status,
  count,
  defaultOpen = true,
  children,
  sticky = true,
  selectAll,
}) {
  const [open, setOpen] = useState(count > 0 ? defaultOpen : false);
  const t = statusTone(status);
  const headerCls = sticky
    ? 'sticky top-0 z-10 shadow-sm'
    : '';

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div
        className={`w-full px-3 py-2 flex items-center gap-2 transition ${headerCls}`}
        style={{ background: t.bg }}
      >
        {selectAll && (
          <input
            type="checkbox"
            checked={!!selectAll.checked}
            ref={(el) => { if (el) el.indeterminate = !!selectAll.indeterminate && !selectAll.checked; }}
            onChange={(e) => { e.stopPropagation(); selectAll.onChange?.(e.target.checked); }}
            className="w-4 h-4 rounded border-slate-300 cursor-pointer"
            style={{ accentColor: t.accent }}
            data-testid={`mcc-group-selectall-${status}`}
            title={`Select all in ${t.label}`}
          />
        )}
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center gap-2 text-left"
          data-testid={`mcc-group-toggle-${status}`}
        >
          {open ? <ChevronDown className="w-4 h-4" style={{ color: t.ink }} />
                : <ChevronRight className="w-4 h-4" style={{ color: t.ink }} />}
          <span
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider"
            style={{ color: t.ink }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.accent }} />
            {t.label}
          </span>
          <span
            className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/60"
            style={{ color: t.ink }}
          >{count}</span>
        </button>
      </div>
      {open && count > 0 && (
        <div className="border-t border-slate-100">
          {children}
        </div>
      )}
      {open && count === 0 && (
        <p className="text-xs text-slate-500 text-center py-3 border-t border-slate-100">Nothing here.</p>
      )}
    </div>
  );
}
